import { getThemeForKey } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button } from "../button/Button";
import { ButtonVariant } from "../button/ButtonVariant";
import { Component } from "../..";
import { HorizontalSpacer } from "../../page/HorizontalSpacer";
import { Panel } from "../panel/Panel";
import { PanelProps } from "../panel/PanelProps";
import { Tab } from "./Tab";
import { TabSelectorPanel } from "./TabSelectorPanel";
import { TabStyle } from "./TabsetProps";

export class TabSelectorPanelAccordion extends TabSelectorPanel {
    private static readonly themeObject = getThemeForKey("tabset.headingAccordion") ?? {};
    private _buttonExpand: Button;
    private _expanded: boolean;
    private horizontalSpacer: HorizontalSpacer;

    constructor(tab: Tab, props?: Partial<PanelProps>) {
        super(tab, {
            ...TabSelectorPanelAccordion.themeObject,
            rowBreak: true,
            ...props
        });
        this.horizontalSpacer = new HorizontalSpacer({ id: this.createInnerComponentId("hs") });
        this.add(this.horizontalSpacer);
        this.addClickListener(() => this.tab.toggleExpanded());
        this.syncExpandButton();
        this.addResizeListener(() => this.sizeLabel());
    }

    override syncInnerComponentIds() {
        super.syncInnerComponentIds();
        if (this.buttonExpand != null) {
            this.buttonExpand.id = this.tab.createInnerComponentId("buttonExpand");
        }
    }

    get buttonExpand(): Button {
        return this._buttonExpand;
    }

    private set buttonExpand(value: Button) {
        this._buttonExpand = value;
    }

    get expanded(): boolean {
        return this._expanded ?? false;
    }

    set expanded(value: boolean) {
        this.expandCollapseTab(value, true);
    }

    expandCollapseTab(value: boolean, animate: boolean) {
        this._expanded = value;
        if (this.tab.tabStyle !== TabStyle.SCROLL) {
            const tabsetContent = this.parent as Panel;
            //not showing the animation is necessary/useful when we don't want a delay in the action being performed
            //this is needed when we are initially setting up the tabset/tab...if we use the animation the delay can mean
            //that tabs are incorrectly expanded/collapsed because we may come through this method > 1 time as the
            //default accordion mode is defaulted/changed
            if (this.expanded) {
                tabsetContent.insert(this.tab, tabsetContent.indexOf(this) + 1);
                if (animate === true)
                    this.tab.expand();
                else
                    this.tab.expandNow();
                this.tab.selectInDesigner();
            }
            else {
                if (animate === true)
                    this.tab.collapse().then(() => tabsetContent.remove(this.tab));
                else {
                    this.tab.collapseNow();
                    tabsetContent.remove(this.tab);
                }
            }
            this.syncExpandButton();
        }
    }

    toggleExpanded() {
        this.expanded = !this.expanded;
    }

    private syncExpandButton() {
        if (this.buttonExpand == null) {
            this.buttonExpand = new Button({
                id: this.tab.createInnerComponentId("buttonExpand"),
                imageName: ImageName.chevronDown,
                variant: ButtonVariant.round,
                color: "subtle.darker",
                rowBreak: false,
                onClick: () => this.toggleExpanded()
            });
            this.buttonExpand.style.transitionDuration = "500ms";
        }
        if (!this.contains(this.buttonExpand)) {
            this.add(this.buttonExpand);
        }
        this.syncChevronRotation();
    }

    private syncChevronRotation() {
        if (this.expanded)
            this.buttonExpand.imageRotation = 0;
        else
            this.buttonExpand.imageRotation = 90;
    }

    get providedComponents(): Component[] {
        const result = super.providedComponents;
        if (this.buttonExpand != null) {
            result.push(this.buttonExpand);
        }
        if (this.horizontalSpacer != null) {
            result.push(this.horizontalSpacer);
        }
        return result;
    }

    override get insertComponentsBeforeThis(): Component {
        return this.horizontalSpacer ?? this.buttonExpand;
    }

    protected override applyHoverClass() {
        // Defeat application of hover class
    }
}
